import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatBilingualLanguageLabel, SORTED_LANGUAGE_OPTIONS, uploadSessionFilesWithProgress, apiResponseError, parseJsonResponse } from '../constants';
import ElapsedTimer from './ElapsedTimer';
import SovereignReportViewer from './SovereignReportViewer';
import Tier1ReportChat from './Tier1ReportChat';
import { ensureHtmlReportToc } from '../utils/sovereignReportHtml';
import FileDropZone from './FileDropZone';

const ACCEPT_KNOWLEDGE =
  '.pdf,application/pdf';

// No MIME/extension filter — operators may upload any export the backend can ingest.
const ACCEPT_SOCIAL = '*/*';

function fileRecordToDisplay(record) {
  if (!record?.filename) return null;
  return { name: record.filename };
}

const SOCIAL_MEDIA_EXTENSIONS = new Set(['.csv', '.xls', '.xlsx']);
const EMPTY_UPLOADED_FILES = [];

function inferUploadKind(record) {
  if (record?.upload_kind === 'social_media') {
    return 'social_media';
  }
  const name = (record?.filename || '').toLowerCase();
  const dot = name.lastIndexOf('.');
  if (dot >= 0 && SOCIAL_MEDIA_EXTENSIONS.has(name.slice(dot))) {
    return 'social_media';
  }
  return record?.upload_kind || 'knowledge';
}

function splitSavedUploads(records) {
  const list = records || [];
  const knowledge = list.find((item) => inferUploadKind(item) !== 'social_media') || null;
  const social = list.find((item) => inferUploadKind(item) === 'social_media') || null;
  return { knowledge, social };
}

export default function SimplifiedTier1Workspace({
  apiFetch,
  sessionId,
  screenLanguage = 'en',
  reportLanguage = 'en',
  onScreenLanguageChange,
  onReportLanguageChange,
  onSaveLanguages,
  onError,
  savedReportHtml,
  savedReportTitle,
  savedUploadedFiles = EMPTY_UPLOADED_FILES,
  onUploadedFilesChange,
  savedTier1Draft = null,
  onTier1DraftChange,
  savedReportChatMessages = [],
  onReportChatMessagesChange,
  onConversationRefresh,
  onReportHtmlUpdated,
  onAnalysisRunningChange,
}) {
  const [reportName, setReportName] = useState('');
  const [pretextLimit, setPretextLimit] = useState('');
  const [missionThreat, setMissionThreat] = useState(false);
  const [missionLocation, setMissionLocation] = useState(false);
  const [knowledgeFile, setKnowledgeFile] = useState(null);
  const [knowledgeSummary, setKnowledgeSummary] = useState('');
  const [knowledgeConfirmed, setKnowledgeConfirmed] = useState(false);
  const [socialMediaFile, setSocialMediaFile] = useState(null);
  const [socialMediaSummary, setSocialMediaSummary] = useState('');
  const [socialMediaConfirmed, setSocialMediaConfirmed] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [running, setRunning] = useState(false);
  const [analysisStartedAt, setAnalysisStartedAt] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [htmlReport, setHtmlReport] = useState(null);
  const [reportPreviewKey, setReportPreviewKey] = useState(0);
  const [reportTitle, setReportTitle] = useState('');
  const [translatingReport, setTranslatingReport] = useState(false);
  const [showClearWorkspaceConfirm, setShowClearWorkspaceConfirm] = useState(false);
  const [regeneratingReport, setRegeneratingReport] = useState(false);
  const [readerWide, setReaderWide] = useState(false);
  const knowledgeInputRef = useRef(null);
  const socialInputRef = useRef(null);
  const pollRef = useRef(null);
  const cancelledRef = useRef(false);
  const formTopRef = useRef(null);
  const draftHydratedRef = useRef(false);
  const prevSessionIdRef = useRef(null);
  const lastAppliedDraftKeyRef = useRef('');
  const skipDraftSaveRef = useRef(true);

  const missionSelected = missionThreat || missionLocation;
  const reportNameReady = Boolean(reportName.trim());
  const canStartAnalysis = missionSelected && reportNameReady && knowledgeConfirmed && !uploading;

  useEffect(() => {
    onAnalysisRunningChange?.(running);
  }, [running, onAnalysisRunningChange]);

  useEffect(() => {
    if (prevSessionIdRef.current === sessionId) {
      return;
    }
    prevSessionIdRef.current = sessionId;
    draftHydratedRef.current = false;
    skipDraftSaveRef.current = true;
    lastAppliedDraftKeyRef.current = '';
    setUploading(false);
    setUploadProgress(0);
    setRunning(false);
    setAnalysisStartedAt(null);
    setProgressMessage('');
    setProgressPercent(0);
    setReaderWide(false);
    if (knowledgeInputRef.current) {
      knowledgeInputRef.current.value = '';
    }
    if (socialInputRef.current) {
      socialInputRef.current.value = '';
    }
  }, [sessionId]);

  useEffect(() => {
    const draft = savedTier1Draft || {};
    const draftKey = JSON.stringify({
      report_name: draft.report_name || '',
      pretext_limit: draft.pretext_limit || '',
      mission_threat_assessment: Boolean(draft.mission_threat_assessment),
      mission_fugitive_location: Boolean(draft.mission_fugitive_location),
    });
    if (lastAppliedDraftKeyRef.current === draftKey) {
      return;
    }
    lastAppliedDraftKeyRef.current = draftKey;
    setReportName(draft.report_name || '');
    setPretextLimit(draft.pretext_limit || '');
    setMissionThreat(Boolean(draft.mission_threat_assessment));
    setMissionLocation(Boolean(draft.mission_fugitive_location));
    draftHydratedRef.current = true;
    skipDraftSaveRef.current = true;
  }, [sessionId, savedTier1Draft]);

  useEffect(() => {
    const { knowledge, social } = splitSavedUploads(savedUploadedFiles);
    if (knowledge) {
      setKnowledgeFile(fileRecordToDisplay(knowledge));
      setKnowledgeConfirmed(true);
      setKnowledgeSummary(knowledge.summary || '');
    } else {
      setKnowledgeFile(null);
      setKnowledgeConfirmed(false);
      setKnowledgeSummary('');
    }
    if (social) {
      setSocialMediaFile(fileRecordToDisplay(social));
      setSocialMediaConfirmed(true);
      setSocialMediaSummary(social.summary || '');
    } else {
      setSocialMediaFile(null);
      setSocialMediaConfirmed(false);
      setSocialMediaSummary('');
    }
  }, [sessionId, savedUploadedFiles]);

  useEffect(() => {
    if (!draftHydratedRef.current || !onTier1DraftChange) {
      return undefined;
    }
    if (skipDraftSaveRef.current) {
      skipDraftSaveRef.current = false;
      return undefined;
    }
    const timer = window.setTimeout(() => {
      onTier1DraftChange({
        report_name: reportName,
        pretext_limit: pretextLimit,
        mission_threat_assessment: missionThreat,
        mission_fugitive_location: missionLocation,
        investigator_notes: savedTier1Draft?.investigator_notes || '',
      });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    reportName,
    pretextLimit,
    missionThreat,
    missionLocation,
    onTier1DraftChange,
    savedTier1Draft,
  ]);

  useEffect(() => {
    setHtmlReport(savedReportHtml || null);
    setReportTitle(savedReportTitle || '');
  }, [sessionId, savedReportHtml, savedReportTitle]);

  const persistUploadedFiles = useCallback((knowledgeRecord, socialRecord) => {
    const next = [knowledgeRecord, socialRecord].filter(Boolean);
    onUploadedFilesChange?.(next);
  }, [onUploadedFilesChange]);

  const uploadWorkspaceFile = async (file, uploadKind) => {
    if (!file || uploading || running) return;
    if (uploadKind === 'knowledge' && !String(file.name || '').toLowerCase().endsWith('.pdf')) {
      onError?.('Knowledge dossier must be a PDF.');
      return;
    }

    const isKnowledge = uploadKind === 'knowledge';
    if (isKnowledge) {
      setKnowledgeFile(null);
      setKnowledgeSummary('');
      setKnowledgeConfirmed(false);
    } else {
      setSocialMediaFile(null);
      setSocialMediaSummary('');
      setSocialMediaConfirmed(false);
    }
    setUploadProgress(0);
    if (isKnowledge) {
      setHtmlReport(null);
    }
    onError?.(null);
    setUploading(true);
    setUploadingKind(uploadKind);

    try {
      const data = await uploadSessionFilesWithProgress({
        sessionId,
        files: [file],
        uploadKind,
        onProgress: setUploadProgress,
      });
      const ok = (data.files || []).find((f) => f.filename === file.name);
      if (!ok) {
        throw new Error('File upload did not complete. Try again.');
      }
      const record = {
        filename: ok.filename || file.name,
        file_type: ok.file_type || '',
        summary: ok.content_summary || data.message || 'File uploaded.',
        upload_kind: uploadKind,
      };
      if (isKnowledge) {
        setKnowledgeFile(file);
        setKnowledgeConfirmed(true);
        setKnowledgeSummary(record.summary);
        persistUploadedFiles(record, splitSavedUploads(savedUploadedFiles).social);
      } else {
        setSocialMediaFile(file);
        setSocialMediaConfirmed(true);
        setSocialMediaSummary(record.summary);
        persistUploadedFiles(splitSavedUploads(savedUploadedFiles).knowledge, record);
      }
      setUploadProgress(100);
      if (isKnowledge && knowledgeInputRef.current) {
        knowledgeInputRef.current.value = '';
      }
      if (!isKnowledge && socialInputRef.current) {
        socialInputRef.current.value = '';
      }
    } catch (err) {
      onError?.(err.message || 'Upload failed');
      if (isKnowledge) {
        setKnowledgeFile(null);
        setKnowledgeConfirmed(false);
        setKnowledgeSummary('');
        if (knowledgeInputRef.current) knowledgeInputRef.current.value = '';
      } else {
        setSocialMediaFile(null);
        setSocialMediaConfirmed(false);
        setSocialMediaSummary('');
        if (socialInputRef.current) socialInputRef.current.value = '';
      }
    } finally {
      setUploading(false);
      setUploadingKind(null);
    }
  };

  const handleFileChange = (event, uploadKind) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadWorkspaceFile(file, uploadKind);
    }
  };

  const handleDropFiles = (fileList, uploadKind) => {
    const file = fileList?.[0];
    if (file) {
      uploadWorkspaceFile(file, uploadKind);
    }
  };

  const pollStatus = useCallback(async () => {
    const params = new URLSearchParams({ session_id: sessionId, _t: String(Date.now()) });
    const response = await apiFetch(`/api/reports/generate/status?${params}`, {
      cache: 'no-store',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail?.message || errorData.detail || 'Status check failed');
    }
    return response.json();
  }, [apiFetch, sessionId]);

  useEffect(() => {
    if (!sessionId || savedReportHtml || running) {
      return undefined;
    }
    let cancelled = false;

    (async () => {
      try {
        const status = await pollStatus();
        if (cancelled || status.status !== 'running') {
          return;
        }
        setRunning(true);
        setProgressMessage(status.message || 'Analysis in progress…');
        setProgressPercent(status.progress || 0);
        if (status.started_at) {
          setAnalysisStartedAt(status.started_at);
        }
        const deadline = Date.now() + 45 * 60 * 1000;
        while (Date.now() < deadline && !cancelled) {
          const next = await pollStatus();
          if (cancelled) return;
          setProgressMessage(next.message || 'Analysis in progress…');
          setProgressPercent(next.progress || 0);
          if (next.status === 'completed') {
            const html = next.html_content || next.report?.html_content;
            const title = next.report_title || next.report?.title;
            if (html) {
              setHtmlReport(html);
              setReportPreviewKey((key) => key + 1);
              if (title) setReportTitle(title);
              onReportHtmlUpdated?.({ html, title, report: next.report });
            }
            break;
          }
          if (next.status === 'failed') {
            onError?.(next.error || next.message || 'Analysis failed.');
            break;
          }
          if (next.status === 'cancelled') {
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Could not resume in-flight Tier 1 analysis:', err);
        }
      } finally {
        if (!cancelled) {
          setRunning(false);
          setProgressMessage('');
          setProgressPercent(0);
          setAnalysisStartedAt(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, savedReportHtml, running, pollStatus, onReportHtmlUpdated, onError]);

  const downloadHtml = () => {
    if (!htmlReport) return;
    const blob = new Blob([ensureHtmlReportToc(htmlReport, reportTitle || reportName)], {
      type: 'text/html;charset=utf-8',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(reportTitle || reportName || 'tier1-report').replace(/[^\w-]+/g, '_')}.html`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetAnalysisForm = useCallback(() => {
    setReportName('');
    setPretextLimit('');
    setMissionThreat(false);
    setMissionLocation(false);
    setKnowledgeFile(null);
    setKnowledgeSummary('');
    setKnowledgeConfirmed(false);
    setSocialMediaFile(null);
    setSocialMediaSummary('');
    setSocialMediaConfirmed(false);
    setUploadingKind(null);
    setUploading(false);
    setUploadProgress(0);
    setRunning(false);
    setAnalysisStartedAt(null);
    setProgressMessage('');
    setProgressPercent(0);
    setHtmlReport(null);
    setReportTitle('');
    onError?.(null);
    onReportHtmlUpdated?.({ html: null, title: null, cleared: true });
    onUploadedFilesChange?.([]);
    onTier1DraftChange?.({
      report_name: '',
      pretext_limit: '',
      mission_threat_assessment: false,
      mission_fugitive_location: false,
    });
    if (knowledgeInputRef.current) knowledgeInputRef.current.value = '';
    if (socialInputRef.current) socialInputRef.current.value = '';
  }, [onError, onReportHtmlUpdated, onUploadedFilesChange, onTier1DraftChange]);

  const handleNewAnalysis = () => {
    if (running) return;
    setShowClearWorkspaceConfirm(true);
  };

  const handleConfirmClearWorkspace = () => {
    setShowClearWorkspaceConfirm(false);
    resetAnalysisForm();
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleStart = async ({ regenerate = false } = {}) => {
    const name = reportName.trim();
    if (!name) {
      onError?.('Enter a name for the report.');
      return;
    }
    if (!missionThreat && !missionLocation) {
      onError?.('Select at least one mission focus checkbox.');
      return;
    }
    if (!knowledgeConfirmed || !knowledgeFile) {
      onError?.(uploading ? 'Wait for the Knowledge PDF upload to finish.' : 'Upload a Knowledge PDF dossier.');
      return;
    }

    const limitRaw = pretextLimit.trim();
    let tier1MaxPretexts = null;
    if (limitRaw) {
      const parsed = Number.parseInt(limitRaw, 10);
      if (!Number.isFinite(parsed) || parsed < 1 || parsed > 10) {
        onError?.('Pretext limit must be a whole number from 1 to 10, or leave blank for the maximum.');
        return;
      }
      tier1MaxPretexts = parsed;
    }

    cancelledRef.current = false;
    setRunning(true);
    setRegeneratingReport(Boolean(regenerate && htmlReport));
    setAnalysisStartedAt(Date.now());
    if (!regenerate) {
      setHtmlReport(null);
    }
    setProgressMessage(regenerate ? 'Regenerating report…' : 'Starting analysis…');
    setProgressPercent(5);
    onError?.(null);

    try {
      await onSaveLanguages?.();

      const response = await apiFetch('/api/reports/generate', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          mode: 'nation_state',
          specialty: 'tier_1_defense',
          pipeline_mode: 'tier1_simple',
          report_title: name,
          report_language: reportLanguage,
          export_language_mode: 'target_only',
          tier1_mission_threat_assessment: missionThreat,
          tier1_mission_fugitive_location: missionLocation,
          ...(tier1MaxPretexts != null ? { tier1_max_pretexts: tier1MaxPretexts } : {}),
        }),
      });
      if (!response.ok && response.status !== 202) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail?.message || errorData.detail || 'Failed to start analysis');
      }

      const deadline = Date.now() + 120 * 60 * 1000;
      while (Date.now() < deadline) {
        if (cancelledRef.current) {
          break;
        }
        const status = await pollStatus();
        if (cancelledRef.current) {
          break;
        }
        if (status.message) setProgressMessage(status.message);
        if (typeof status.progress === 'number') setProgressPercent(status.progress);
        if (status.status === 'completed') {
          let html = status.report?.html_content;
          if (!html) {
            try {
              const convResponse = await apiFetch(`/api/conversations/${sessionId}`, { cache: 'no-store' });
              if (convResponse.ok) {
                const conversation = await convResponse.json();
                html = conversation.report?.html_content || '';
              }
            } catch {
              // Fall through to the error below.
            }
          }
          if (!html) {
            throw new Error(
              'Analysis finished but no HTML report was returned. Start a new analysis run — earlier runs before the Tier 1 update may need to be regenerated.',
            );
          }
          setHtmlReport(html);
          setReportTitle(status.report?.title || name);
          onReportHtmlUpdated?.({
            html,
            title: status.report?.title || name,
          });
          setProgressMessage('Analysis complete.');
          setProgressPercent(100);
          break;
        }
        if (status.status === 'failed') {
          throw new Error(status.error || status.message || 'Analysis failed');
        }
        if (status.status === 'cancelled') {
          throw new Error('Analysis was cancelled.');
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
    } catch (err) {
      if (!cancelledRef.current) {
        onError?.(err.message || 'Analysis failed');
      }
    } finally {
      setRunning(false);
      setRegeneratingReport(false);
      setAnalysisStartedAt(null);
      cancelledRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    }
  };

  const handleRegenerateReport = () => {
    handleStart({ regenerate: true });
  };

  const handleStopAnalysis = async () => {
    if (!running) return;
    cancelledRef.current = true;
    setProgressMessage('Stopping analysis…');
    try {
      await apiFetch('/api/reports/generate/cancel', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId }),
      });
    } catch (err) {
      onError?.(err.message || 'Failed to stop analysis');
    }
  };

  const handleTranslateReport = async () => {
    if (!htmlReport || running || translatingReport) return;
    const target = (reportLanguage || '').trim();
    if (!target || target === 'auto') {
      onError?.('Choose a specific report language before translating.');
      return;
    }
    setTranslatingReport(true);
    onError?.(null);
    setProgressMessage('Starting translation…');
    setProgressPercent(5);
    cancelledRef.current = false;
    try {
      await onSaveLanguages?.();
      const response = await apiFetch('/api/reports/tier1/translate', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          target_language: target,
        }),
      });
      if (!response.ok && response.status !== 202) {
        const errorData = await parseJsonResponse(response, {});
        throw new Error(apiResponseError(response, errorData, 'Report translation failed.'));
      }

      const pollTranslateStatus = async () => {
        const params = new URLSearchParams({ session_id: sessionId, _t: String(Date.now()) });
        const statusResponse = await apiFetch(`/api/reports/tier1/translate/status?${params}`, {
          cache: 'no-store',
        });
        if (!statusResponse.ok) {
          const errorData = await parseJsonResponse(statusResponse, {});
          throw new Error(apiResponseError(statusResponse, errorData, 'Translation status check failed.'));
        }
        return parseJsonResponse(statusResponse, {});
      };

      const deadline = Date.now() + 120 * 60 * 1000;
      while (Date.now() < deadline) {
        if (cancelledRef.current) {
          break;
        }
        const status = await pollTranslateStatus();
        if (cancelledRef.current) {
          break;
        }
        if (status.message) setProgressMessage(status.message);
        if (typeof status.progress === 'number') setProgressPercent(status.progress);
        if (status.status === 'completed') {
          const html = status.html_content || status.report?.html_content;
          if (!html) {
            throw new Error('Translation finished but no HTML report was returned.');
          }
          const title = status.report?.title || reportTitle;
          setHtmlReport(html);
          setReportPreviewKey((key) => key + 1);
          if (title) setReportTitle(title);
          onReportHtmlUpdated?.({
            html,
            title,
            report: status.report,
            reportSourceLanguage: status.report_source_language,
            reportTargetLanguage: status.report_target_language,
          });
          setProgressMessage('Translation complete.');
          setProgressPercent(100);
          break;
        }
        if (status.status === 'failed') {
          throw new Error(status.error || status.message || 'Report translation failed.');
        }
        if (status.status === 'cancelled') {
          throw new Error('Report translation was cancelled.');
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    } catch (err) {
      if (!cancelledRef.current) {
        onError?.(err.message || 'Report translation failed.');
      }
    } finally {
      setTranslatingReport(false);
      cancelledRef.current = false;
    }
  };

  const reportLanguageOption =
    SORTED_LANGUAGE_OPTIONS.find((opt) => opt.code === reportLanguage) || {
      code: reportLanguage,
      label: reportLanguage,
    };
  const reportLanguageLabel = formatBilingualLanguageLabel(reportLanguageOption);

  return (
    <div className="w-full min-w-0">
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <div className="max-w-xl mx-auto space-y-6 rounded-xl bg-legend-paper text-legend-navy border border-legend-teal/20 p-6" ref={formTopRef}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-legend-navy mb-1">Tier 1 Analysis</h1>
              <p className="text-sm text-legend-slate">
                Defense, offense, and elicitation assessment from your uploaded dossier.
              </p>
            </div>
            {htmlReport && !running && (
              <button
                type="button"
                onClick={handleNewAnalysis}
                className="px-3 py-2 rounded-lg bg-white border border-legend-navy/20 text-legend-navy text-sm hover:bg-legend-paper shrink-0"
              >
                Clear workspace
              </button>
            )}
          </div>

          <label className="block">
            <span className="text-sm text-legend-navy mb-1 block">Report name</span>
            <input
              type="text"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              disabled={running}
              placeholder="e.g. March assessment (subject last name added from dossier)"
              className="w-full px-3 py-2 rounded-lg bg-white border border-legend-teal/30 text-legend-navy text-sm focus:ring-2 focus:ring-legend-teal"
            />
          </label>

          <fieldset className="space-y-3 rounded-lg border border-legend-teal/20 bg-white p-4">
            <legend className="text-sm text-legend-navy px-1">Mission focus</legend>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={missionThreat}
                onChange={(e) => setMissionThreat(e.target.checked)}
                disabled={running}
                className="mt-1 rounded border-legend-slate bg-white text-legend-teal focus:ring-legend-teal/40"
              />
              <span className="text-sm text-legend-navy leading-snug">
                Elicitation of Potential Nation State Threat
                <span className="block text-xs text-legend-slate mt-1">
                  Subject whereabouts are known. Focus elicitation on the subject, family, friends, and organizations for threat assessment.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={missionLocation}
                onChange={(e) => setMissionLocation(e.target.checked)}
                disabled={running}
                className="mt-1 rounded border-legend-slate bg-white text-legend-teal focus:ring-legend-teal/40"
              />
              <span className="text-sm text-legend-navy leading-snug">
                Location of Nation State Fugitive via Elicitation
                <span className="block text-xs text-legend-slate mt-1">
                  Subject location is unknown. Use elicitation against family, friends, and organizations to locate the subject or determine alive, deceased, or unknown status.
                </span>
              </span>
            </label>
          </fieldset>

          <label className="block">
            <span className="text-sm text-legend-navy mb-1 block">Limit Number of Playbooks</span>
            <input
              type="number"
              min={1}
              max={10}
              value={pretextLimit}
              onChange={(e) => setPretextLimit(e.target.value)}
              disabled={running}
              placeholder="Optional, up to 10 (blank = maximum)"
              className="w-full px-3 py-2 rounded-lg bg-white border border-legend-teal/30 text-legend-navy text-sm focus:ring-2 focus:ring-legend-teal"
            />
            <span className="text-xs text-legend-slate mt-1 block">
              Leave blank to generate playbooks for all targets (maximum 10).
            </span>
          </label>

          <label className="block">
            <span className="text-sm text-legend-navy mb-1 block">Screen Language</span>
            <select
              value={screenLanguage}
              onChange={(e) => onScreenLanguageChange?.(e.target.value)}
              disabled={running}
              className="w-full px-3 py-2 rounded-lg bg-white border border-legend-teal/30 text-legend-navy text-sm"
            >
              {SORTED_LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {formatBilingualLanguageLabel(opt)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm text-legend-navy mb-1 block">Report Language</span>
            <select
              value={reportLanguage}
              onChange={(e) => onReportLanguageChange?.(e.target.value)}
              disabled={running}
              className="w-full px-3 py-2 rounded-lg bg-white border border-legend-teal/30 text-legend-navy text-sm"
            >
              {SORTED_LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {formatBilingualLanguageLabel(opt)}
                </option>
              ))}
            </select>
          </label>

          <div className="space-y-4">
            <div>
              <span className="text-sm text-legend-navy mb-2 block">Knowledge dossier</span>
              <input
                ref={knowledgeInputRef}
                type="file"
                accept={ACCEPT_KNOWLEDGE}
                onChange={(event) => handleFileChange(event, 'knowledge')}
                disabled={running || uploading}
                className="hidden"
              />
              <FileDropZone
                disabled={running || uploading}
                onFiles={(files) => handleDropFiles(files, 'knowledge')}
                className="rounded-lg border border-dashed border-legend-teal/40 bg-white p-3"
                activeClassName="border-legend-teal bg-legend-teal/10"
              >
                <button
                  type="button"
                  onClick={() => knowledgeInputRef.current?.click()}
                  disabled={running || uploading}
                  className="w-full px-4 py-2.5 rounded-lg bg-legend-teal hover:bg-legend-teal-dark disabled:opacity-50 text-white text-sm font-medium"
                >
                  Upload Knowledge PDF
                </button>
                <p className="text-xs text-legend-slate mt-2 text-center">
                  Drag and drop a PDF here, or browse.
                </p>
              </FileDropZone>
              {uploading && uploadingKind === 'knowledge' && (
                <p className="text-xs text-legend-teal-dark mt-2">Uploading Knowledge PDF… {uploadProgress}%</p>
              )}
              {knowledgeConfirmed && knowledgeFile && !uploading && (
                <p className="text-xs text-legend-teal-dark mt-2 truncate">
                  {knowledgeFile.name} - uploaded
                </p>
              )}
              {knowledgeSummary && !uploading && uploadingKind !== 'knowledge' && (
                <p className="text-xs text-legend-slate mt-1 line-clamp-3">{knowledgeSummary}</p>
              )}
            </div>

            <div>
              <span className="text-sm text-legend-navy mb-1 block">Social media (optional)</span>
              <p className="text-xs text-legend-slate mb-2">
                Upload posts, messages, or exports in any supported format (CSV, Excel, PDF, TXT, JSON, ZIP, etc.). DISC analysis runs when you click Regenerate Report, not on upload alone.
              </p>
              <input
                ref={socialInputRef}
                type="file"
                accept={ACCEPT_SOCIAL}
                onChange={(event) => handleFileChange(event, 'social_media')}
                disabled={running || uploading}
                className="hidden"
              />
              <FileDropZone
                disabled={running || uploading}
                onFiles={(files) => handleDropFiles(files, 'social_media')}
                className="rounded-lg border border-dashed border-legend-teal/40 bg-white p-3"
                activeClassName="border-legend-teal bg-legend-teal/10"
              >
                <button
                  type="button"
                  onClick={() => socialInputRef.current?.click()}
                  disabled={running || uploading}
                  className="w-full px-4 py-2.5 rounded-lg bg-white hover:bg-legend-paper border border-legend-teal/40 disabled:opacity-50 text-legend-teal-dark text-sm font-medium"
                >
                  Social Media Upload
                </button>
                <p className="text-xs text-legend-slate mt-2 text-center">
                  Drag and drop a dump here, or browse.
                </p>
              </FileDropZone>
              {uploading && uploadingKind === 'social_media' && (
                <p className="text-xs text-legend-teal-dark mt-2">Uploading social media file… {uploadProgress}%</p>
              )}
              {socialMediaConfirmed && socialMediaFile && !uploading && (
                <p className="text-xs text-legend-teal-dark mt-2 truncate">
                  {socialMediaFile.name} - uploaded
                </p>
              )}
              {socialMediaSummary && socialMediaConfirmed && !uploading && (
                <p className="text-xs text-legend-slate mt-1 line-clamp-3">{socialMediaSummary}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => handleStart({ regenerate: Boolean(htmlReport) })}
              disabled={running || !canStartAnalysis}
              className="flex-1 py-3 rounded-lg bg-legend-teal hover:bg-legend-teal-dark disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors"
            >
              {running
                ? (regeneratingReport ? 'Regenerating report…' : 'Running analysis…')
                : htmlReport
                  ? 'Regenerate Report and Playbooks'
                  : 'Start Analysis and Playbook Creation'}
            </button>
            {running && (
              <button
                type="button"
                onClick={handleStopAnalysis}
                className="sm:w-auto px-4 py-3 rounded-lg bg-red-900/80 hover:bg-red-800 border border-red-500/40 text-red-100 font-semibold text-sm transition-colors"
              >
                Stop Analysis
              </button>
            )}
          </div>

          {running && (
            <div className="rounded-lg border border-legend-teal/20 bg-white p-4">
              <div className="h-2 rounded-full bg-legend-paper overflow-hidden mb-2">
                <div
                  className="h-full bg-legend-teal transition-all duration-300"
                  style={{ width: `${Math.min(100, progressPercent)}%` }}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-legend-slate">{progressMessage}</p>
                {analysisStartedAt != null && (
                  <ElapsedTimer startedAt={analysisStartedAt} className="text-legend-teal-dark" />
                )}
              </div>
            </div>
          )}
        </div>

        {htmlReport && (
          <div className={`${readerWide ? 'w-full' : 'max-w-5xl mx-auto'} mt-10 space-y-4`}>
            {regeneratingReport && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-950/30 px-4 py-3 text-sm text-amber-100">
                Regenerating in this session. Your previous report stays visible below until the new run finishes.
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-legend-paper">{reportTitle}</h2>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 text-sm text-legend-paper">
                  <span className="sr-only">Report language</span>
                  <select
                    value={reportLanguage}
                    onChange={(e) => onReportLanguageChange?.(e.target.value)}
                    disabled={running || translatingReport}
                    className="px-2 py-2 rounded-lg bg-legend-navy border border-legend-teal/30 text-legend-paper text-sm"
                    aria-label="Report language"
                  >
                    {SORTED_LANGUAGE_OPTIONS.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {formatBilingualLanguageLabel(opt)}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={handleTranslateReport}
                  disabled={running || translatingReport}
                  className="px-4 py-2 rounded-lg bg-indigo-700 hover:bg-indigo-600 disabled:opacity-50 text-white text-sm font-medium"
                >
                  {translatingReport ? 'Translating…' : `Translate to ${reportLanguageLabel}`}
                </button>
                <button
                  type="button"
                  onClick={handleRegenerateReport}
                  disabled={running || translatingReport || !canStartAnalysis}
                  className="px-4 py-2 rounded-lg bg-legend-teal hover:bg-legend-teal-dark disabled:opacity-50 text-white text-sm font-medium"
                >
                  {running && regeneratingReport ? 'Regenerating…' : 'Regenerate Report'}
                </button>
                <button
                  type="button"
                  onClick={() => setReaderWide((value) => !value)}
                  className="px-4 py-2 rounded-lg bg-legend-navy border border-legend-teal/30 text-legend-teal-tint text-sm hover:bg-legend-deep"
                >
                  {readerWide ? 'Narrow reader' : 'Widen reader'}
                </button>
                <button
                  type="button"
                  onClick={downloadHtml}
                  disabled={translatingReport}
                  className="px-4 py-2 rounded-lg bg-legend-navy border border-legend-teal/30 text-legend-teal-tint text-sm hover:bg-legend-deep disabled:opacity-50"
                >
                  Download HTML
                </button>
              </div>
            </div>
            {translatingReport && (
              <div className="rounded-lg border border-indigo-500/20 bg-gray-900/80 p-4">
                <div className="h-2 rounded-full bg-gray-800 overflow-hidden mb-2">
                  <div
                    className="h-full bg-indigo-500 transition-all duration-300"
                    style={{ width: `${Math.min(100, progressPercent)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400">{progressMessage || 'Translating report…'}</p>
              </div>
            )}
            <div
              key={reportPreviewKey}
              className={`w-full ${readerWide ? 'min-h-[80vh]' : 'min-h-[28rem]'} rounded-lg border border-legend-teal/20 bg-legend-navy scroll-mt-4`}
            >
              <SovereignReportViewer
                htmlContent={htmlReport}
                reportTitle={reportTitle}
                onDownloadHtml={downloadHtml}
                savedTier1Draft={savedTier1Draft}
                onTier1DraftChange={onTier1DraftChange}
              />
            </div>
            <Tier1ReportChat
              apiFetch={apiFetch}
              sessionId={sessionId}
              reportLanguage={reportLanguage}
              disabled={running || translatingReport}
              savedMessages={savedReportChatMessages}
              onMessagesChange={onReportChatMessagesChange}
              onConversationRefresh={onConversationRefresh}
              onHtmlUpdated={({ html, title }) => {
                setHtmlReport(html);
                setReportPreviewKey((key) => key + 1);
                if (title) setReportTitle(title);
                onReportHtmlUpdated?.({ html, title });
              }}
              onError={onError}
            />
          </div>
        )}
      </div>

      {showClearWorkspaceConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-legend-deep/80 px-4">
          <div
            className="max-w-md w-full rounded-lg border border-legend-teal/30 bg-legend-navy p-5 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="clear-workspace-title"
          >
            <h2 id="clear-workspace-title" className="text-base font-semibold text-legend-paper mb-3">
              Clear this workspace?
            </h2>
            <p className="text-sm text-legend-paper/90 leading-relaxed">
              This removes the report, uploads, and form fields from the current session. It does not delete the session from History.
              To re-run on the same dossier, use <strong className="text-legend-teal-tint">Regenerate Report</strong> instead.
              For a new subject, use <strong className="text-legend-teal-tint">New Chat</strong> in the header.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowClearWorkspaceConfirm(false)}
                className="px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmClearWorkspace}
                className="px-4 py-2 rounded-lg bg-red-900/80 hover:bg-red-800 border border-red-500/40 text-red-100 text-sm font-medium"
              >
                Clear workspace
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
